import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Bell,
  CircleAlert,
  Info,
  Languages as LanguagesIcon,
  LayoutGrid as LayoutGridIcon,
  Megaphone,
  Tag as TagIcon,
} from "lucide-react";
import { cn } from "../../../../lib/utils.js";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../../../../components/ui/tooltip/index.js";
import {
  isTemplateNameValid,
  sanitizeTemplateName,
  TEMPLATE_LIMITS,
} from "../../template-editor-presentation.js";
import type { TemplateFormState } from "../template-management-form.js";
import { TemplateLanguageSelect } from "../TemplateLanguageSelect/index.js";
import type { TemplateCreationCategory } from "../template-creation-wizard-state.js";

/** New-template categories only. Authentication stays syncable and viewable elsewhere. */
const CREATION_CATEGORIES = ["MARKETING", "UTILITY"] as const satisfies readonly TemplateCreationCategory[];

const CATEGORY_ICONS: Record<
  (typeof CREATION_CATEGORIES)[number],
  React.ComponentType<{ size?: number; className?: string }>
> = {
  MARKETING: Megaphone,
  UTILITY: Bell,
};

type NameStatus = "idle" | "empty" | "invalid";

export interface TemplateDefinitionStepProps {
  category: TemplateCreationCategory | null;
  formState: TemplateFormState;
  onCategoryChange: (category: TemplateCreationCategory) => void;
  onFormChange: (next: TemplateFormState) => void;
}

function SectionLabel({
  icon: Icon,
  title,
  hint,
}: {
  icon: React.ComponentType<{ size?: number; className?: string }>;
  title: string;
  hint: string;
}): React.JSX.Element {
  return (
    <div className="flex items-start gap-2.5 text-start">
      <span
        className="flex size-7 shrink-0 items-center justify-center rounded-md bg-surface-muted text-ink-muted"
        aria-hidden="true"
      >
        <Icon size={14} />
      </span>
      <div className="grid min-w-0 gap-0.5">
        <span className="text-sm font-semibold text-ink">{title}</span>
        <span className="text-xs leading-snug text-ink-muted">{hint}</span>
      </div>
    </div>
  );
}

export function TemplateDefinitionStep({
  category,
  formState,
  onCategoryChange,
  onFormChange,
}: TemplateDefinitionStepProps): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const isHebrewUi = i18n.language === "he";
  const [nameStatus, setNameStatus] = useState<NameStatus>("idle");

  const handleCategoryChange = useCallback(
    (next: TemplateCreationCategory) => {
      onCategoryChange(next);
    },
    [onCategoryChange],
  );

  function handleNameBlur(): void {
    if (!formState.name.trim()) setNameStatus("empty");
    else if (!isTemplateNameValid(formState.name)) setNameStatus("invalid");
    else setNameStatus("idle");
  }

  return (
    <div className="mx-auto grid w-full max-w-2xl gap-4 sm:gap-5">
      <div className="grid gap-1">
        <h2 className="m-0 text-lg font-semibold tracking-tight text-ink">
          {t("whatsapp.templates.manage.wizard.definitionTitle")}
        </h2>
        <p className="m-0 text-sm leading-snug text-ink-muted">
          {t("whatsapp.templates.manage.wizard.definitionDescription")}
        </p>
      </div>

      <div className="grid gap-3">
      <section className="grid gap-3 rounded-xl border border-line bg-surface p-4 shadow-sm sm:gap-4 sm:p-5">
        <SectionLabel
          icon={TagIcon}
          title={t("whatsapp.templates.manage.fields.name")}
          hint={t("whatsapp.templates.manage.wizard.nameSectionHint")}
        />
        <div className="grid min-w-0 gap-1.5">
        <div className="relative" dir="ltr">
          <input
            dir="ltr"
            className={cn(
              "h-10 w-full min-w-0 rounded-md border bg-surface py-2 pe-16 ps-3 text-left text-sm text-ink [unicode-bidi:isolate]",
              nameStatus === "empty" || nameStatus === "invalid"
                ? "border-error"
                : "border-line focus:border-accent",
            )}
            value={formState.name}
            maxLength={TEMPLATE_LIMITS.name}
            placeholder={t("whatsapp.templates.manage.wizard.editor.namePlaceholder")}
            autoComplete="off"
            spellCheck={false}
            onChange={(event) => {
              if (nameStatus !== "idle") setNameStatus("idle");
              onFormChange({
                ...formState,
                name: sanitizeTemplateName(event.target.value),
              });
            }}
            onBlur={handleNameBlur}
          />
          <span className="pointer-events-none absolute end-3 top-1/2 -translate-y-1/2 text-xs text-ink-muted">
            {formState.name.length}/{TEMPLATE_LIMITS.name}
          </span>
        </div>
        <p className="m-0 min-h-4 text-xs text-ink-muted">
          {nameStatus === "empty" ? (
            <span className="inline-flex items-center gap-1 text-error">
              <CircleAlert size={12} aria-hidden="true" />
              {t("whatsapp.templates.manage.wizard.editor.nameError")}
            </span>
          ) : nameStatus === "invalid" ? (
            <span className="inline-flex items-center gap-1 text-error">
              <CircleAlert size={12} aria-hidden="true" />
              {t("whatsapp.templates.manage.wizard.editor.nameFormatError")}
            </span>
          ) : (
            t("whatsapp.templates.manage.fields.nameHelp")
          )}
        </p>
        </div>
      </section>

      <section className="grid gap-3 rounded-xl border border-line bg-surface p-4 shadow-sm sm:gap-4 sm:p-5">
        <SectionLabel
          icon={LayoutGridIcon}
          title={t("whatsapp.templates.manage.fields.category")}
          hint={t("whatsapp.templates.manage.wizard.categorySectionHint")}
        />
        <div
          className="grid min-w-0 gap-2 sm:grid-cols-2"
          role="radiogroup"
          aria-label={t("whatsapp.templates.manage.fields.category")}
        >
          {CREATION_CATEGORIES.map((cat) => {
            const Icon = CATEGORY_ICONS[cat];
            const isSelected = cat === category;
            return (
              <div
                key={cat}
                className={cn(
                  "flex items-start gap-2 rounded-lg border px-3 py-3 text-start transition-colors",
                  isSelected
                    ? "border-accent bg-accent-soft"
                    : "border-line bg-surface hover:bg-surface-hover",
                )}
              >
                <button
                  type="button"
                  role="radio"
                  aria-checked={isSelected}
                  onClick={() => handleCategoryChange(cat)}
                  className="flex min-w-0 flex-1 items-start gap-2.5 text-start focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                >
                  <span
                    className={cn(
                      "mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-md",
                      isSelected ? "bg-accent text-white" : "bg-surface-muted text-ink-muted",
                    )}
                  >
                    <Icon size={16} aria-hidden="true" />
                  </span>
                  <span className="grid min-w-0 gap-0.5">
                    <span className="text-sm font-medium text-ink">
                      {t(`whatsapp.templates.manage.wizard.categories.${cat}`)}
                    </span>
                    <span className="text-xs leading-snug text-ink-muted">
                      {t(`whatsapp.templates.manage.wizard.categoryHints.${cat}`)}
                    </span>
                  </span>
                </button>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className="mt-0.5 shrink-0 text-ink-subtle hover:text-ink"
                      aria-label={t(`whatsapp.templates.manage.wizard.categories.${cat}`)}
                    >
                      <Info size={14} aria-hidden="true" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs text-start text-xs">
                    {t(`whatsapp.templates.manage.wizard.categoryHints.${cat}`)}
                  </TooltipContent>
                </Tooltip>
              </div>
            );
          })}
        </div>
      </section>

      <section className="grid gap-3 rounded-xl border border-line bg-surface p-4 shadow-sm sm:gap-4 sm:p-5">
        <SectionLabel
          icon={LanguagesIcon}
          title={t("whatsapp.templates.manage.language")}
          hint={t("whatsapp.templates.manage.wizard.languageSectionHint")}
        />
        <div className={cn("w-full max-w-sm min-w-0", isHebrewUi && "justify-self-start")}>
          <TemplateLanguageSelect
            value={formState.language}
            onChange={(language) => onFormChange({ ...formState, language })}
            englishOnly
            hideCodes
            radioSelection
            compact
          />
        </div>
      </section>
      </div>
    </div>
  );
}
